/* eslint-disable global-require */

import React from 'react';
import styled from 'styled-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import Section from '@latitude/section';
import { Text } from '@latitude/text';
import { ALIGN } from '@/utils/constants';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

import imgExpediaOffer from './images/expedia-offer.webp';
import imgWotifOffer from './images/wotif-offer.webp';

const DEFAULT_OFFERS = [
  {
    image: imgExpediaOffer,
    imageAlt: 'Expedia',
    title: 'Enjoy a 10% discount at Expedia',
    description:
      'on selected hotels all day, every day, year-round. Offer may be withdrawn. Exclusions and T&Cs apply.'
  },
  {
    image: imgWotifOffer,
    imageAlt: 'Wotif',
    title: 'Enjoy a 10% discount at Wotif',
    description:
      'on selected hotels all day, every day, year-round. Offer may be withdrawn. Exclusions and T&Cs apply.'
  }
];

const getRichTextPlainText = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(getRichTextPlainText).join('');
  if (value.value) return value.value;
  if (value.content) return getRichTextPlainText(value.content);
  return '';
};

const richTextInlineOptions = getCdrEntryBindingRichTextOptions({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node, children) => <>{children}</>,
    [BLOCKS.HEADING_3]: (_node, children) => <>{children}</>
  }
});

const renderRichTextInline = value => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;

  return value?.nodeType === 'document'
    ? documentToReactComponents(value, richTextInlineOptions)
    : getRichTextPlainText(value);
};

const getAssetUrl = image => {
  const url = image?.file?.url;
  if (!url) return undefined;
  return url.startsWith('//') ? `https:${url}` : url;
};

const getAssetAlt = (image, fallback) =>
  image?.description || image?.title || image?.file?.fileName || fallback;

const getOfferItems = data => {
  if (!data?.cards?.length) return DEFAULT_OFFERS;

  return data.cards.map((card, index) => {
    const fallback = DEFAULT_OFFERS[index] || {};

    return {
      image: getAssetUrl(card?.image) || fallback.image,
      imageAlt: getAssetAlt(card?.image, fallback.imageAlt),
      title: renderRichTextInline(card?.title) || fallback.title,
      description: renderRichTextInline(card?.description) || fallback.description
    };
  });
};

function ExpediaWotifSection({ anchor, heading, data }) {
  const offerItems = getOfferItems(data);
  const sectionHeading = getRichTextPlainText(data?.title) || heading;
  const description =
    getRichTextPlainText(data?.description) ||
    "We've partnered with the world's best to help you save and get more out of every holiday. It's just another benefit of paying with Latitude!";

  return (
    <Section id={anchor} heading={sectionHeading}>
      <div
        css={{
          alignContent: 'center',
          justifyContent: 'center',
          margin: '10pt auto 10pt auto',
          padding: '5pt',
          maxWidth: '640px'
        }}
      >
        <Text align={ALIGN.CENTER}>{description}</Text>
      </div>
      <div
        css={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '10pt',
          margin: '10pt auto'
        }}
      >
        {offerItems.map((offer, index) => (
          <OfferItem key={offer.imageAlt || index}>
            <img src={offer.image} alt={offer.imageAlt} />
            <h3>{offer.title}</h3>
            <p>{offer.description}</p>
          </OfferItem>
        ))}
        {/* <OfferItem>
          <img src={imgHotelsCom} alt="Hotels.com" height="196" />
          <h3>Enjoy a 6% discount at Hotels.com</h3>
          <p>
            on selected hotels all day, every day, year-round. Offer may be
            withdrawn. Exclusions and T&Cs apply.
          </p>
        </OfferItem> */}
      </div>
    </Section>
  );
}

const OfferItem = styled.div`
  background-color: #ffffff;
  flex-basis: 200px;
  margin: 0;
  padding: 0;
  text-align: center;
  border: 1px solid #ddd;
  border-bottom: 3px solid #888;
  h3 {
    color: #000000;
    font-size: 20px;
    font-weight: bold;
    line-height: 24px;
  }
  p {
    padding: 5px;
  }
  img {
    width: 100%;
    height: auto;
  }
`;

export default ExpediaWotifSection;
